import { useEffect, useState, useRef, useCallback, forwardRef, useImperativeHandle } from "react";
import { deleteAutomation, getAutomationLabel, addAutomation, updateAutomation, addAutomationFolder, deleteAutomationFolder, AutomationFolder, fetchFolderById, fetchAutomationById, updateAutomationFolderName, searchAutomations } from "../api/automation";
import { Button } from "./ui/button";
import { Trash2, FileSpreadsheet, Info, X, Plus, MoreVertical, Loader2, Folder, FolderPlus, Edit, Clock } from "lucide-react";
import { VariableModal } from "./VariableModal";
import "../styles/AutomationList.css";
import { ReactComponent as ExcelIcon } from '../excel.svg';
import { ReactComponent as FormIcon } from '../form.svg';
import { useAuth } from "../context/AuthContext";
import { Loader } from "./ui/loader";
import Toast from "./automation/Toast";
import { fetchAutomationTree, AutomationFolderTreeDto, AutomationWithFolderDto } from '../api/automation';
import { searchFreeTemplates } from '../api/automation';
import { useDropzone } from "react-dropzone";

// Types
interface Automation {
  id: string;
  title: string;
  type?: string;
  createdAt?: string;
  automationTemplateDocument?: { id: string; fileName?: string; title?: string };
  autofillVariables?: string[];
  folderId?: string;
}

export interface AutomationListRefType {
  jumpToFolderAndSearch: (automationName: string, folderId: string | null, automationId?: string) => void;
}

type AutomationListProps = {
  automationSearch: string;
  setAutomationSearch: (val: string) => void;
};

const AutomationList = forwardRef<AutomationListRefType, AutomationListProps>(
  function AutomationList({ automationSearch, setAutomationSearch }, ref) {
    const { user } = useAuth();
    const tenantId = user?.tenantId;

    // State declarations
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState("");
    const [isSearching, setIsSearching] = useState(false);
    const [searchResults, setSearchResults] = useState<AutomationWithFolderDto[]>([]);
    const [searchBreadcrumbs, setSearchBreadcrumbs] = useState<{ id: string | null, name: string }[]>([]);
    const [sortBy, setSortBy] = useState<"title" | "type" | "createdAt">("createdAt");
    const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
    const [selectedAutomation, setSelectedAutomation] = useState<Automation | null>(null);
    const [isVariableModalOpen, setIsVariableModalOpen] = useState(false);
    const [isAgendaOpen, setIsAgendaOpen] = useState(false);
    const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
    const [isAddAutomationOpen, setIsAddAutomationOpen] = useState(false);
    const [selectedDocumentId, setSelectedDocumentId] = useState("");
    const [automationTitle, setAutomationTitle] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [searchDoc, setSearchDoc] = useState("");
    const [isDocDropdownOpen, setIsDocDropdownOpen] = useState(false);
    const docInputRef = useRef(null);
    const [isEditMode, setIsEditMode] = useState(false);
    const [editingAutomation, setEditingAutomation] = useState<Automation | null>(null);
    const [isExcelModalOpen, setIsExcelModalOpen] = useState(false);
    const [excelFile, setExcelFile] = useState<File | null>(null);
    const [excelFileName, setExcelFileName] = useState("");
    const [isExcelSubmitting, setIsExcelSubmitting] = useState(false);
    const [excelToast, setExcelToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
    const [currentFolderId, setCurrentFolderId] = useState<string | null>(null);
    const [breadcrumbs, setBreadcrumbs] = useState<{ id: string | null, name: string }[]>([{ id: null, name: 'Wszystkie Automatyzacje' }]);
    const [showAddMenu, setShowAddMenu] = useState(false);
    const folderCache = useRef<Map<string, AutomationFolder>>(new Map());
    const [isAddFolderOpen, setIsAddFolderOpen] = useState(false);
    const [newFolderName, setNewFolderName] = useState("");
    const [isDeletingFolder, setIsDeletingFolder] = useState<string | null>(null);
    const [isEditingFolder, setIsEditingFolder] = useState<AutomationFolder | null>(null);
    const [editFolderName, setEditFolderName] = useState("");
    const [draggedAutomationId, setDraggedAutomationId] = useState<string | null>(null);
    const [hoveredRowId, setHoveredRowId] = useState<string | null>(null);
    const [isFolderAndAutomationsLoading, setIsFolderAndAutomationsLoading] = useState(false);
    const [dragOverBreadcrumbId, setDragOverBreadcrumbId] = useState<string | null>(null);
    const [showOnlyThisAutomation, setShowOnlyThisAutomation] = useState(false);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const [searchSuggestions, setSearchSuggestions] = useState<Automation[]>([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [isFullSearch, setIsFullSearch] = useState(false);
    const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1); // NEW: index podświetlonej podpowiedzi
    const [isEditingFolderLoading, setIsEditingFolderLoading] = useState(false); // loading dla edycji folderu
    const [editFolderError, setEditFolderError] = useState<string | null>(null); // error dla edycji folderu
    const [blockSearchEffect, setBlockSearchEffect] = useState(false); // flaga blokująca useEffect na searchQuery
    const [blockFolderEffect, setBlockFolderEffect] = useState(false); // flaga blokująca useEffect na currentFolderId
    const [automationTree, setAutomationTree] = useState<AutomationFolderTreeDto[] | null>(null);
    const [freeTemplates, setFreeTemplates] = useState<{id: string, fileName: string}[]>([]);
    const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
    const [isAddFolderLoading, setIsAddFolderLoading] = useState(false);
    // Dodaj nowy stan na wybraną automatyzację do Excela
    const [selectedExcelAutomation, setSelectedExcelAutomation] = useState<Automation | null>(null);
    const [customLocation, setCustomLocation] = useState(false);

    const {
      getRootProps: getExcelRootProps,
      getInputProps: getExcelInputProps,
      isDragActive: isExcelDragActive
    } = useDropzone({
      accept: {
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
        'application/vnd.ms-excel': ['.xls']
      },
      maxFiles: 1,
      maxSize: 10 * 1024 * 1024,
      multiple: false,
      onDrop: (acceptedFiles) => {
        if (acceptedFiles.length > 0) {
          setExcelFile(acceptedFiles[0]);
          setExcelFileName(acceptedFiles[0].name);
        }
      }
    });

    // Dodaj tutaj deklarację hooka useCallback:
    const loadAutomationTree = useCallback(async () => {
      if (!tenantId) return;
      setIsFolderAndAutomationsLoading(true);
      try {
        const tree = await fetchAutomationTree(tenantId);
        setAutomationTree(tree);
      } catch (e) {
        setToast({ message: "Błąd ładowania drzewa automatyzacji.", type: "error" });
      } finally {
        setIsFolderAndAutomationsLoading(false);
      }
    }, [tenantId]);

    // Helper: budowanie breadcrumbs rekurencyjnie
    const buildBreadcrumbs = useCallback(async (tenantId: string, folder: AutomationFolder | null): Promise<{ id: string | null, name: string }[]> => {
      const crumbs: { id: string | null, name: string }[] = [];
      let current = folder;
      while (current) {
        crumbs.unshift({ id: current.id, name: current.name });
        if (current.parentId) {
          current = await fetchFolderById(tenantId, current.parentId);
        } else {
          current = null;
        }
      }
      crumbs.unshift({ id: null, name: 'Wszystkie Automatyzacje' });
      return crumbs;
    }, []);

    // Fetch automations for the current folder (no pagination)
    const loadAutomations = useCallback(async (overrideSearch?: string) => {
      if (!tenantId) return;
      try {
        setLoading(true);
        const data = await searchAutomations(tenantId, overrideSearch ?? "");
        setSearchResults(data);
      } catch (error) {
        setToast({ message: "Błąd ładowania automatyzacji.", type: "error" });
      } finally {
        setLoading(false);
      }
    }, [tenantId]);

    // Nowa funkcja do pobierania podpowiedzi (top 5)
    const fetchSuggestions = useCallback(async (query: string) => {
      if (!tenantId || !query) return;
      try {
        const data = await searchAutomations(tenantId, query);
        setSearchSuggestions(data);
      } catch {
        setSearchSuggestions([]);
      }
    }, [tenantId]);

    // Nowa funkcja do pełnego wyszukiwania
    const fetchFullSearch = useCallback(async (query: string) => {
      if (!tenantId || !query) return;
      setLoading(true);
      setIsSearching(true);
      setIsFullSearch(true);
      try {
        const data = await searchAutomations(tenantId, query);
        // Dla każdego wyniku pobierz pełne dane automatyzacji
        const fullResults = await Promise.all(
          data.map(async (a) => {
            try {
              return await fetchAutomationById(tenantId, a.id);
            } catch {
              return a;
            }
          })
        );
        setSearchResults(fullResults as AutomationWithFolderDto[]);
        setSearchBreadcrumbs([{ id: null, name: 'Wyniki wyszukiwania' }]);
      } catch {
        setSearchResults([]);
        setSearchBreadcrumbs([{ id: null, name: 'Wyniki wyszukiwania' }]);
      } finally {
        setLoading(false);
      }
    }, [tenantId]);

    // Obsługa inputa: podpowiedzi + enter
    useEffect(() => {
      if (blockSearchEffect) {
        setBlockSearchEffect(false); // odblokuj po pierwszym renderze
        return;
      }
      if (showOnlyThisAutomation) return;
      if (searchQuery.trim() === "") {
        setIsSearching(false);
        setIsFullSearch(false);
        setSearchResults([]);
        setSearchSuggestions([]);
        setSearchBreadcrumbs([]);
        setShowSuggestions(false);
        setShowOnlyThisAutomation(false);
        loadAutomations();
        return;
      }
      setShowSuggestions(true);
      const timeout = setTimeout(() => {
        fetchSuggestions(searchQuery.trim());
      }, 200);
      return () => clearTimeout(timeout);
    }, [searchQuery]);

    // Kliknięcie poza dropdown zamyka podpowiedzi
    useEffect(() => {
      function handleClickOutside(e: MouseEvent) {
        if (searchInputRef.current && !(searchInputRef.current as any).contains(e.target)) {
          setShowSuggestions(false);
        }
      }
      if (showSuggestions) {
        document.addEventListener('click', handleClickOutside);
      }
      return () => document.removeEventListener('click', handleClickOutside);
    }, [showSuggestions]);

    // Obsługa Enter w input
    function handleSearchInputKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
      if (showSuggestions && searchSuggestions.length > 0) {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          setSelectedSuggestionIndex(idx => Math.min(idx + 1, searchSuggestions.length - 1));
          return;
        }
        if (e.key === 'ArrowUp') {
          e.preventDefault();
          setSelectedSuggestionIndex(idx => Math.max(idx - 1, 0));
          return;
        }
        if (e.key === 'Enter') {
          if (selectedSuggestionIndex >= 0 && selectedSuggestionIndex < searchSuggestions.length) {
            handleSuggestionMouseDown(searchSuggestions[selectedSuggestionIndex]);
            return;
          }
          setShowSuggestions(false);
          fetchFullSearch(searchQuery.trim());
          return;
        }
        if (e.key === 'Escape') {
          setShowSuggestions(false);
          return;
        }
      }
      if (e.key === 'Enter' && searchQuery.trim()) {
        setShowSuggestions(false);
        fetchFullSearch(searchQuery.trim());
      }
    }

    // Resetuj selectedSuggestionIndex przy zmianie podpowiedzi/searchQuery
    useEffect(() => {
      setSelectedSuggestionIndex(-1);
    }, [searchSuggestions, searchQuery, showSuggestions]);

    // Kliknięcie w podpowiedź
    async function handleSuggestionMouseDown(automation: Automation) {
      setShowSuggestions(false);
      if (!tenantId) return;

      // Pobierz pełne dane automatyzacji przed ustawieniem w wynikach
      let fullAutomation: Automation | null = null;
      try {
        fullAutomation = await fetchAutomationById(tenantId, automation.id);
      } catch (e) {
        // fallback do tego co mamy, jeśli nie uda się pobrać
        fullAutomation = automation;
      }

      if (fullAutomation.folderId) {
        try {
          const folder = await fetchFolderById(tenantId, fullAutomation.folderId);
          const crumbs = await buildBreadcrumbs(tenantId, folder);
          setBreadcrumbs(crumbs);
          setCurrentFolderId(fullAutomation.folderId);
          setSearchBreadcrumbs(crumbs);
        } catch {
          setBreadcrumbs([{ id: null, name: 'Wszystkie Automatyzacje' }]);
          setCurrentFolderId(null);
          setSearchBreadcrumbs([{ id: null, name: 'Wszystkie Automatyzacje' }]);
        }
      } else {
        setBreadcrumbs([{ id: null, name: 'Wszystkie Automatyzacje' }]);
        setCurrentFolderId(null);
        setSearchBreadcrumbs([{ id: null, name: 'Wszystkie Automatyzacje' }]);
      }

      setSearchResults([fullAutomation] as AutomationWithFolderDto[]);
      setIsFullSearch(true);
      setIsSearching(true);
      setSearchQuery(fullAutomation.title);
      setAutomationSearch(fullAutomation.title);
      // NIE ustawiaj selectedAutomation tutaj!
    }

    // Kliknięcie "Zobacz wszystkie wyniki"
    function handleShowAllResultsMouseDown() {
      setShowSuggestions(false);
      if (searchSuggestions.length === 1) {
        setSearchResults(searchSuggestions as AutomationWithFolderDto[]);
        setSearchBreadcrumbs([{ id: null, name: 'Wyniki wyszukiwania' }]);
        setIsFullSearch(true);
        setIsSearching(true);
        return;
      }
      fetchFullSearch(searchQuery.trim());
    }

    // Synchronizuj breadcrumbs i currentFolder z currentFolderId
    useEffect(() => {
      if (blockFolderEffect) {
        setBlockFolderEffect(false); // odblokuj po pierwszym renderze
        return;
      }
      if (!tenantId) return;
      if (currentFolderId) {
        fetchFolderById(tenantId, currentFolderId).then(folder => {
          buildBreadcrumbs(tenantId, folder).then(setBreadcrumbs);
        });
      } else {
        setBreadcrumbs([{ id: null, name: 'Wszystkie Automatyzacje' }]);
      }
    }, [tenantId, currentFolderId, buildBreadcrumbs]);

    useImperativeHandle(ref, () => ({
      jumpToFolderAndSearch: async (automationName: string, folderId: string | null, automationId?: string) => {
        if (!tenantId) return;
        if (automationId) {
          setLoading(true);
          setBlockSearchEffect(true); // blokuj useEffect na searchQuery
          setBlockFolderEffect(true); // blokuj useEffect na currentFolderId
          const targetAutomation = await fetchAutomationById(tenantId, automationId);
          const targetFolderId = targetAutomation.folderId || null;
          let crumbs = [{ id: null, name: 'Wszystkie Automatyzacje' }];
          if (targetFolderId) {
            const folder = await fetchFolderById(tenantId, targetFolderId);
            buildBreadcrumbs(tenantId, folder).then(setBreadcrumbs);
            setCurrentFolderId(targetFolderId);
            crumbs = await buildBreadcrumbs(tenantId, folder);
            setBreadcrumbs(crumbs);
          } else {
            setBreadcrumbs(crumbs);
          }
          setSearchQuery(targetAutomation.title);
          setAutomationSearch(targetAutomation.title);
          setShowSuggestions(false); // Ukryj dropdown natychmiast
          setSearchBreadcrumbs([{ id: null, name: 'Wyniki wyszukiwania' }]);
          showFullSearchForAutomation(targetAutomation, crumbs);
          setShowSuggestions(false); // Ukryj dropdown jeszcze raz po zmianie stanu
          setLoading(false);
          return;
        }
        setCurrentFolderId(folderId);
        if (folderId) {
          const folder = await fetchFolderById(tenantId, folderId);
          buildBreadcrumbs(tenantId, folder).then(setBreadcrumbs);
          const crumbs = await buildBreadcrumbs(tenantId, folder);
          setBreadcrumbs(crumbs);
        } else {
          setBreadcrumbs([{ id: null, name: 'Wszystkie Automatyzacje' }]);
        }
        setSearchQuery(automationName || "");
        setAutomationSearch(automationName || "");
        setShowOnlyThisAutomation(false);
        await loadAutomations(automationName);
      }
    }), [tenantId, loadAutomations, setAutomationSearch, buildBreadcrumbs, showFullSearchForAutomation]);

    useEffect(() => {
      if (isAddAutomationOpen) {
        if (isEditMode && editingAutomation) {
          setSelectedDocumentId(editingAutomation.automationTemplateDocument?.id || "");
          setSearchDoc(editingAutomation.automationTemplateDocument?.fileName || "");
        }
      }
    }, [isAddAutomationOpen, isEditMode, editingAutomation]);

    useEffect(() => {
      function handleClickOutside(e: MouseEvent) {
        if (docInputRef.current && !(docInputRef.current as any).contains(e.target)) {
          setIsDocDropdownOpen(false);
        }
      }
      if (isDocDropdownOpen) {
        document.addEventListener('mousedown', handleClickOutside);
      }
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isDocDropdownOpen]);

    // 🔥 Modal dla ręcznego użycia
    useEffect(() => {
      // Modal otwieramy tylko jeśli user kliknie "Formularz" (handleUseManually)
      // Nie otwieraj automatycznie po zmianie selectedAutomation
    }, [selectedAutomation]);

    useEffect(() => {
      setCurrentFolderId(null);
      setBreadcrumbs([{ id: null, name: 'Wszystkie Automatyzacje' }]);
      loadAutomationTree();
    }, [tenantId]);

    useEffect(() => {
      if (!searchQuery.trim()) {
        setSearchSuggestions([]);
        return;
      }
      const timeout = setTimeout(async () => {
        if (!tenantId) return;
        const results = await searchAutomations(tenantId, searchQuery.trim());
        setSearchSuggestions(results);
      }, 200);
      return () => clearTimeout(timeout);
    }, [searchQuery, tenantId]);

    // Poprawiona funkcja do wyszukiwania węzła po id, zgodna z nowym API i typem:
    function findNodeById(nodes: AutomationFolderTreeDto[], id: string | null): AutomationFolderTreeDto & { automations: any[] } | null {
      if (id === null) {
        // Szukamy root node (id: null)
        const rootNode = nodes.find(n => n.id === null);
        // Wszystkie foldery na najwyższym poziomie (parentId: null, id !== null)
        const topFolders = nodes.filter(n => n.parentId === null && n.id !== null);
        // Automatyzacje bez folderu (folderId: null)
        let automations: any[] = [];
        if (rootNode && Array.isArray(rootNode.automations)) {
          automations = [...rootNode.automations];
        }
        // Dodatkowo: automatyzacje z innych węzłów, które mają folderId: null
        for (const node of nodes) {
          if (node.id !== null && Array.isArray(node.automations)) {
            automations.push(...node.automations.filter(a => a.folderId === null));
          }
        }
        return {
          id: null,
          name: 'Wszystkie Automatyzacje',
          tenantId: '',
          createdAt: '',
          parentId: null,
          children: topFolders,
          automations
        } as AutomationFolderTreeDto & { automations: any[] };
      }
      for (const node of nodes) {
        if (node.id === id) return node as AutomationFolderTreeDto & { automations: any[] };
        const found = findNodeById(node.children, id);
        if (found) return found;
      }
      return null;
    }

    const currentNode = automationTree ? findNodeById(automationTree, currentFolderId) : null;
    const folders = currentNode ? currentNode.children : [];
    const automations = currentNode ? currentNode.automations : [];

    const automationsList: Automation[] = (automations as Automation[]);

    const handleUseManually = (automation: Automation) => {
      if (!selectedAutomation || selectedAutomation.id !== automation.id) {
        setSelectedAutomation(automation);
      }
      setIsVariableModalOpen(true);
    };

    const handleDeleteAutomation = async (id: string) => {
      try {
        if (!tenantId) return;
        await deleteAutomation(tenantId, id);
        await loadAutomations(); // przeładuj listę po usunięciu
        await loadAutomationTree(); // przeładuj drzewo po usunięciu
        setToast({ message: "Automatyzacja usunięta!", type: "success" });
      } catch (error) {
        setToast({ message: "Błąd usuwania automatyzacji.", type: "error" });
      }
    };

    const handleExcelSubmit = async () => {
      if (!excelFile || !tenantId || !selectedExcelAutomation?.id) return;
      setIsExcelSubmitting(true);
      try {
        const formData = new FormData();
        formData.append("file", excelFile);
        formData.append("customLocation", customLocation.toString());
        let response = await fetch(`/api/tenants/${tenantId}/automations/${selectedExcelAutomation.id}/upload-excel`, {
          method: "POST",
          body: formData,
          credentials: 'include',
        });
        if (!response.ok) throw new Error("Błąd wczytywania pliku Excel");
        setExcelToast({ message: "Umowy zostały wygenerowane na podstawie pliku Excel!", type: "success" });
        setTimeout(() => {
          setIsExcelModalOpen(false);
          setExcelFile(null);
          setExcelFileName("");
          setCustomLocation(false);
        }, 1200);
      } catch (error) {
        setExcelToast({ message: "Błąd wczytywania pliku Excel.", type: "error" });
      } finally {
        setIsExcelSubmitting(false);
      }
    };

    // Fix: Accept automationId as argument
    const handleDownloadExcelTemplate = async (automationId?: string) => {
      if (!tenantId || !automationId) return;
      try {
        const response = await fetch(`/api/tenants/${tenantId}/automations/${automationId}/download-excel-template`, { credentials: 'include' });
        if (!response.ok) throw new Error("Błąd pobierania szablonu Excel");
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "szablon_automatyzacji.xlsx";
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } catch (error) {
        setExcelToast({ message: "Błąd pobierania szablonu Excel.", type: "error" });
      }
    };

    // Update handleBreadcrumbClick to fetch only relevant data for the selected folder
    const handleBreadcrumbClick = (id: string | null) => {
      setCurrentFolderId(id);
      setShowOnlyThisAutomation(false);
      setSearchQuery("");
      setAutomationSearch("");
      setIsFullSearch(false);
      setIsSearching(false);
      setSearchBreadcrumbs([]);
      // NIE wywołuj loadAutomations() tutaj!
    };

    // Update handleAddFolder to refresh only folders for the current folder
    const handleAddFolder = async (name: string) => {
      if (!tenantId) return;
      const parentId = currentFolderId || null;
      try {
        await addAutomationFolder(tenantId, name, parentId);
        await loadAutomationTree();
      } catch (e) {
        let msg = 'Błąd dodawania folderu.';
        if (e && typeof e === 'object') {
          if ('message' in e && typeof (e as any).message === 'string') {
            msg = (e as any).message;
          } else if (e instanceof Error) {
            msg = e.toString();
          } else {
            msg = JSON.stringify(e);
          }
        } else if (typeof e === 'string') {
          msg = e;
        }
        setToast({ message: msg, type: 'error' });
        throw e;
      }
    };

    // Dodaj funkcję do zmiany folderu automatyzacji
    async function updateAutomationFolder(tenantId: string, automationId: string, folderId: string | null) {
      await updateAutomation(tenantId, automationId, undefined, undefined, folderId === undefined ? null : folderId);
      await loadAutomationTree();
    }

    // Dodaj funkcję:
    function showFullSearchForAutomation(automation: Automation, folderBreadcrumbs?: { id: string | null, name: string }[]) {
      setSearchQuery(automation.title);
      setIsFullSearch(true);
      setIsSearching(true);
      setShowOnlyThisAutomation(false);
      setShowSuggestions(false);
      setSearchResults([]);
      setSearchBreadcrumbs([{ id: null, name: 'Wyniki wyszukiwania' }]);
      if (folderBreadcrumbs) setBreadcrumbs(folderBreadcrumbs);
      fetchFullSearch(automation.title);
    }

    // Funkcja do edycji automatyzacji
    function handleEditAutomation(automation: Automation) {
      setIsEditMode(true);
      setEditingAutomation(automation);
      setIsAddAutomationOpen(true);
      setAutomationTitle(automation.title);
      setSelectedDocumentId(automation.id);
    }

    // Funkcja do edycji folderu
    function handleEditFolder(folder: AutomationFolder) {
      setIsEditingFolder(folder);
      setEditFolderName(folder.name);
    }

    // useEffect do pobierania podpowiedzi szablonów
    useEffect(() => {
      let timeout: NodeJS.Timeout | undefined;
      if (!isAddAutomationOpen) {
        setFreeTemplates([]);
        setIsLoadingTemplates(false);
      } else {
        setIsLoadingTemplates(true);
        timeout = setTimeout(async () => {
          if (!tenantId) return;
          try {
            const results = await searchFreeTemplates(tenantId, searchDoc.trim());
            setFreeTemplates(results);
          } catch {
            setFreeTemplates([]);
          } finally {
            setIsLoadingTemplates(false);
          }
        }, 200);
      }
      return () => { if (timeout) clearTimeout(timeout); };
    }, [searchDoc, isAddAutomationOpen, tenantId]);

    function formatDateTime(dateString) {
      const date = new Date(dateString);
      const now = new Date();
      const day = date.toLocaleDateString('pl-PL');
      const time = date.toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' });
      const full = date.toLocaleString('pl-PL');
      const isToday = date.toDateString() === now.toDateString();
      const yesterday = new Date(now);
      yesterday.setDate(now.getDate() - 1);
      const isYesterday = date.toDateString() === yesterday.toDateString();
      let label = day;
      if (isToday) label = 'Dziś';
      else if (isYesterday) label = 'Wczoraj';
      return { label, time, full };
    }

    if (!tenantId) {
      return <div>Błąd: Brak tenantId</div>;
    }
    if (isFolderAndAutomationsLoading) {
      return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
          <Loader />
        </div>
      );
    }
    
    return (
      <>
        <div className="automation-header enhanced-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative', background: 'linear-gradient(90deg, #e0e7ff 0%, #f8fafc 100%)', borderRadius: 18, boxShadow: '0 2px 16px rgba(37,99,235,0.07)', padding: '18px 32px 18px 24px', minHeight: 64 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 0, marginTop: 0 }}>
            {(isFullSearch && breadcrumbs.length > 0 ? breadcrumbs : (isSearching && searchQuery.trim() && searchBreadcrumbs.length > 0 ? searchBreadcrumbs : breadcrumbs)).map((b, i, arr) => (
              <span
                key={b.id || 'root'}
                style={{
                  color: i === arr.length - 1 ? '#2563eb' : '#888',
                  cursor: 'pointer',
                  fontWeight: i === arr.length - 1 ? 700 : 500,
                  display: 'flex',
                  alignItems: 'center',
                  fontSize: 20,
                  background: dragOverBreadcrumbId === (b.id ?? 'root') ? '#e0e7ff' : 'transparent',
                  borderRadius: dragOverBreadcrumbId === (b.id ?? 'root') ? 10 : 0,
                  padding: dragOverBreadcrumbId === (b.id ?? 'root') ? '4px 12px' : 0,
                  transition: 'background 0.18s',
                  boxShadow: dragOverBreadcrumbId === (b.id ?? 'root') ? '0 2px 8px #c7d2fe' : 'none',
                }}
                onClick={() => (isFullSearch || (isSearching && searchQuery.trim() && searchBreadcrumbs.length > 0) ? handleBreadcrumbClick(b.id) : (!isSearching && i !== arr.length - 1 && handleBreadcrumbClick(b.id)))}
                onDragOver={e => {
                  if (!isSearching) { e.preventDefault(); setDragOverBreadcrumbId(b.id ?? 'root'); }
                }}
                onDragLeave={e => {
                  if (!isSearching) { e.preventDefault(); setDragOverBreadcrumbId(null); }
                }}
                onDrop={async e => {
                  if (!isSearching) {
                    e.preventDefault();
                    setDragOverBreadcrumbId(null);
                    if (draggedAutomationId && tenantId) {
                      await updateAutomationFolder(tenantId, draggedAutomationId, b.id || null);
                      setDraggedAutomationId(null);
                      loadAutomationTree();
                    }
                  }
                }}
              >
                {i > 0 && <span style={{ color: '#bbb', fontWeight: 700, fontSize: 20, margin: '0 6px' }}>/</span>}
                <Folder size={22} style={{ marginRight: 6, color: '#2563eb', filter: 'drop-shadow(0 2px 6px #c7d2fe)' }} /> {b.name}
              </span>
            ))}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap' }}>
            {/* Szukajka */}
            <div style={{ position: 'relative', marginRight: 10, minWidth: 340, width: 340 }}>
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={e => {
                  setSearchQuery(e.target.value);
                  setIsFullSearch(false);
                  setShowOnlyThisAutomation(false);
                }}
                onFocus={() => { if (searchQuery.trim()) setShowSuggestions(true); }}
                onKeyDown={handleSearchInputKeyDown}
                placeholder="Szukaj automatyzacji..."
                style={{
                  padding: '10px 38px 10px 16px',
                  borderRadius: 14,
                  border: '1.5px solid #e0e7ff',
                  fontSize: 16,
                  minWidth: 340,
                  width: 340,
                  background: '#fff',
                  boxShadow: '0 2px 8px #e0e7ff33',
                  outline: 'none',
                  transition: 'border 0.18s, box-shadow 0.18s',
                }}
                autoFocus
                autoComplete="off"
              />
              <svg style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', width: 18, height: 18, color: '#2563eb', pointerEvents: 'none' }} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
              {showSuggestions && searchSuggestions.length > 0 && (
                <div style={{
                  position: 'absolute',
                  left: 0,
                  right: 'auto',
                  top: 44,
                  marginTop: 0,
                  zIndex: 1000,
                  background: '#fff',
                  border: '1.5px solid #e0e7ff',
                  borderRadius: 12,
                  boxShadow: '0 4px 24px #2563eb22',
                  maxHeight: 320,
                  overflowY: 'auto',
                  animation: 'fadeIn 0.18s',
                  width: 540,
                }}>
                  {searchSuggestions.map((s, idx) => (
                    <div
                      key={s.id}
                      style={{
                        padding: '12px 18px',
                        cursor: 'pointer',
                        fontWeight: 600,
                        fontSize: 16,
                        color: '#2563eb',
                        borderBottom: '1px solid #e0e7ff',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        background: idx === selectedSuggestionIndex ? '#e0e7ff' : '#fff',
                        transition: 'background 0.15s',
                      }}
                      onMouseDown={() => handleSuggestionMouseDown(s)}
                      onMouseEnter={() => setSelectedSuggestionIndex(idx)}
                    >
                      <FileSpreadsheet size={18} style={{ marginRight: 8 }} />
                      <span>{s.title}</span>
                      <span style={{ color: '#888', fontWeight: 400, fontSize: 14, marginLeft: 10 }}>
                        {s.folderId && folderCache.current.get(s.folderId) ? `/${folderCache.current.get(s.folderId)?.name}` : ''}
                      </span>
                    </div>
                  ))}
                  <div
                    style={{
                      padding: '12px 18px',
                      cursor: 'pointer',
                      fontWeight: 700,
                      fontSize: 15,
                      color: '#2563eb',
                      background: '#f3f6fd',
                      textAlign: 'center',
                      borderRadius: '0 0 12px 12px',
                    }}
                    onMouseDown={handleShowAllResultsMouseDown}
                  >
                    Zobacz wszystkie wyniki
                  </div>
                </div>
              )}
            </div>
            <Button onClick={() => setIsAgendaOpen(true)} className="info-btn enhanced-secondary-btn" style={{ padding: '12px 28px', fontSize: 16, borderRadius: 22, border: '2px solid #2563eb', background: '#fff', color: '#2563eb', fontWeight: 600, boxShadow: '0 2px 8px #e0e7ff', transition: 'box-shadow 0.18s' }}>
              <Info size={20} style={{ marginRight: 10 }} /> Instrukcje
            </Button>
            <div style={{ position: 'relative', display: 'inline-block' }}>
              <Button onClick={() => setShowAddMenu(v => !v)} className="add-file-btn enhanced-primary-btn" style={{ fontWeight: 700, fontSize: 18, borderRadius: 26, padding: '12px 36px', background: 'linear-gradient(90deg, #2563eb 0%, #60a5fa 100%)', color: '#fff', boxShadow: '0 4px 16px #2563eb33', letterSpacing: '0.5px', border: 'none', transition: 'box-shadow 0.18s, background 0.18s' }}>
                <Plus size={22} style={{ marginRight: 10 }} /> Dodaj
              </Button>
              {showAddMenu && (
                <div style={{ position: 'absolute', right: 0, top: 54, background: '#fff', borderRadius: 16, boxShadow: '0 8px 32px rgba(37,99,235,0.13)', minWidth: 240, zIndex: 1000, padding: '10px 0', animation: 'fadeIn 0.18s' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 28px', cursor: 'pointer', fontWeight: 700, fontSize: 17, color: '#2563eb', borderRadius: 10 }} onClick={() => { setIsAddAutomationOpen(true); setShowAddMenu(false); }}>
                    <FileSpreadsheet size={22} style={{ marginRight: 10 }} /> Dodaj automatyzację
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 28px', cursor: 'pointer', fontWeight: 700, fontSize: 17, color: '#2563eb', borderRadius: 10 }} onClick={() => { setIsAddFolderOpen(true); setShowAddMenu(false); }}>
                    <FolderPlus size={22} style={{ marginRight: 10 }} /> Dodaj folder
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="table-container enhanced-table-container" style={{ position: 'relative', background: '#fff', borderRadius: 18, boxShadow: '0 4px 32px rgba(37,99,235,0.07)', padding: '0 0 18px 0', marginBottom: 24, overflowX: 'auto', overflowY: 'auto' }}>
          <div style={{ height: 8 }} />
          <table className="automation-table enhanced-table" style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
            <thead>
              <tr style={{ background: '#f3f6fd', borderBottom: '2.5px solid #e0e7ff', height: 56 }}>
                <th style={{ fontWeight: 800, fontSize: 17, padding: '16px 0 16px 18px', letterSpacing: '0.02em', color: '#222', textAlign: 'left', textTransform: 'none' }}>Nazwa</th>
                <th style={{ fontWeight: 800, fontSize: 17, padding: '16px 0', color: '#222', textAlign: 'left', textTransform: 'none' }}>Data modyfikacji</th>
                <th style={{ fontWeight: 800, fontSize: 17, padding: '16px 0', color: '#222', textAlign: 'left', textTransform: 'none' }}> Szablon</th>
                <th style={{ fontWeight: 800, fontSize: 17, padding: '16px 0', color: '#222', textAlign: 'left', textTransform: 'none' }}>Typ</th>
                <th style={{ width: 140 }}></th>
                <th style={{ width: 90 }}></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7}><Loader /></td></tr>
              ) : isFullSearch && searchQuery.trim() ? (
                searchResults.length === 0 ? (
                  <tr><td colSpan={3} style={{ textAlign: 'center', color: '#888', fontSize: 16, padding: 10 }}>Brak plików ani folderów.</td></tr>
                ) : (
                  searchResults.map(automation => (
                    <tr
                      key={automation.id}
                      className={`enhanced-table-row automation-row-pro${hoveredRowId === automation.id ? ' row-hover' : ''}`}
                      style={{ background: '#fff', transition: 'background 0.18s', height: 20 }}
                      draggable
                      onMouseEnter={() => setHoveredRowId(automation.id)}
                      onMouseLeave={() => setHoveredRowId(null)}
                      onDragStart={() => setDraggedAutomationId(automation.id)}
                      onDragEnd={() => setDraggedAutomationId(null)}
                    >
                      <td style={{ fontWeight: 700, padding: '0 0 0 18px', minWidth: 140, lineHeight: 1, fontSize: 15, height: 28, verticalAlign: 'middle', cursor: 'pointer', color: '#2563eb', textDecoration: 'underline' }}
                      >
                        <span style={{ display: 'flex', alignItems: 'center' }}>
                          <FileSpreadsheet size={20} color="#1976d2" style={{ flexShrink: 0, marginRight: 8, filter: 'drop-shadow(0 2px 6px #c7d2fe)' }} />
                          {automation.title}
                        </span>
                      </td>
                      <td style={{ minWidth: 90, lineHeight: 1, fontSize: 14, height: 28 }}
                        title={automation.createdAt ? formatDateTime(automation.createdAt).full : ''}
                      >
                        {automation.createdAt ? (
                          <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                            <span style={{ color: '#666', fontWeight: 400, fontSize: 15 }}>{formatDateTime(automation.createdAt).label}</span>
                            <span style={{ color: '#888', marginLeft: 8, display: 'inline-flex', alignItems: 'center', fontWeight: 400, fontSize: 15 }}>
                              <Clock size={14} style={{ marginRight: 3, color: '#2563eb', verticalAlign: 'middle' }} />
                              {formatDateTime(automation.createdAt).time}
                            </span>
                          </span>
                        ) : '—'}
                      </td>
                      <td style={{ minWidth: 80, lineHeight: 1, fontSize: 14, height: 28 }}>{automation.automationTemplateDocument?.title || automation.automationTemplateDocument?.fileName || '—'}</td>
                      <td style={{ minWidth: 80, lineHeight: 1, fontSize: 14, height: 28 }}>{getAutomationLabel(automation.type)}</td>
                      <td style={{ textAlign: 'left', minWidth: 120, lineHeight: 1, height: 28, paddingLeft: 12 }}>
                        <div className="action-buttons" style={{ display: 'flex', gap: 10, justifyContent: 'flex-start', alignItems: 'center', marginRight: 0 }}>
                          <Button className="ghost-template-btn enhanced-chip-btn" style={{padding: '6px 18px', fontSize: 14, height: 32, borderRadius: 18, background: '#f3f6fd', color: '#2563eb', fontWeight: 700, boxShadow: '0 2px 8px #e0e7ff', border: 'none', display: 'flex', alignItems: 'center', gap: 8, transition: 'background 0.18s, box-shadow 0.18s'}} onClick={() => handleUseManually(automation)}>
                            <FormIcon style={{ width: 16, height: 16, marginRight: 0 }} /> Formularz
                          </Button>
                          <Button className="ghost-template-btn enhanced-chip-btn" style={{padding: '6px 18px', fontSize: 14, height: 32, borderRadius: 18, background: '#f3f6fd', color: '#22c55e', fontWeight: 700, boxShadow: '0 2px 8px #e0e7ff', border: 'none', display: 'flex', alignItems: 'center', gap: 8, transition: 'background 0.18s, box-shadow 0.18s'}} onClick={() => { setSelectedExcelAutomation(automation); setIsExcelModalOpen(true); }}>
                            <ExcelIcon style={{ width: 16, height: 16, marginRight: 0 }} /> Excel
                          </Button>
                        </div>
                      </td>
                      <td style={{ textAlign: 'left', minWidth: 80, lineHeight: 1, height: 28, paddingRight: 12 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'flex-start' }}>
                          <button className="icon-button delete enhanced-ghost-btn" title="Usuń automatyzację" style={{ display: 'flex', alignItems: 'center', background: '#f3f6fd', border: 'none', borderRadius: 10, padding: 7, transition: 'background 0.18s, box-shadow 0.18s', boxShadow: '0 2px 8px #e0e7ff', marginRight: 4 }} onClick={() => handleDeleteAutomation(automation.id)}>
                            <Trash2 size={15} />
                          </button>
                          <button className="icon-button edit enhanced-ghost-btn" title="Edytuj automatyzacje" style={{ display: 'flex', alignItems: 'center', background: '#f3f6fd', border: 'none', borderRadius: 10, marginRight: 15, padding: 7, transition: 'background 0.18s, box-shadow 0.18s', boxShadow: '0 2px 8px #e0e7ff' }} onClick={() => {
                            setIsEditMode(true);
                            setEditingAutomation(automation);
                            setAutomationTitle(automation.title);
                            setSelectedDocumentId(automation.id);
                            setIsAddAutomationOpen(true);
                          }}>
                            <MoreVertical size={15} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )
              ) : (
                (() => {
                  if (!currentNode) return <tr><td colSpan={7}>Brak danych</td></tr>;
                  if (folders.length === 0 && automations.length === 0) {
                    return (
                      <tr><td colSpan={3} style={{ textAlign: 'center', color: '#888', fontSize: 16, padding: 10 }}>Brak plików ani folderów.</td></tr>                    );
                  }
                  return [
                    ...folders.map(folder => (
                      <tr
                        key={folder.id}
                        className={`enhanced-table-row folder-row-pro${hoveredRowId === folder.id ? ' row-hover' : ''}`}
                        style={{ cursor: 'pointer', height: 20 }}
                        onMouseEnter={() => setHoveredRowId(folder.id)}
                        onMouseLeave={() => setHoveredRowId(null)}
                        onDoubleClick={() => setCurrentFolderId(folder.id)}
                        onDrop={async e => {
                          e.preventDefault();
                          if (draggedAutomationId && tenantId) {
                            await updateAutomation(tenantId, draggedAutomationId, undefined, undefined, folder.id);
                            setDraggedAutomationId(null);
                            await loadAutomations();
                            await loadAutomationTree();
                          }
                        }}
                      >
                        <td style={{ fontWeight: 700, fontSize: 15, padding: '0 0 0 18px', minWidth: 140, lineHeight: 1, height: 28, verticalAlign: 'middle' }}>
                          <span style={{ display: 'flex', alignItems: 'center' }}>
                            <Folder size={20} color="#2563eb" style={{ flexShrink: 0, marginRight: 8, filter: 'drop-shadow(0 2px 6px #c7d2fe)' }} />
                            <span style={{ color: '#18181b', fontWeight: 700 }}>{folder.name}</span>
                          </span>
                        </td>
                        <td style={{ color: '#18181b', fontWeight: 500, minWidth: 90, lineHeight: 1, fontSize: 14, height: 28 }}
                          title={folder.createdAt ? formatDateTime(folder.createdAt).full : ''}
                        >
                          {folder.createdAt ? (
                            <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                              <span style={{ color: '#666', fontWeight: 400, fontSize: 15 }}>{formatDateTime(folder.createdAt).label}</span>
                              <span style={{ color: '#888', marginLeft: 8, display: 'inline-flex', alignItems: 'center', fontWeight: 400, fontSize: 15 }}>
                                <Clock size={14} style={{ marginRight: 3, color: '#2563eb', verticalAlign: 'middle' }} />
                                {formatDateTime(folder.createdAt).time}
                              </span>
                            </span>
                          ) : '—'}
                        </td>
                        <td style={{ minWidth: 80, lineHeight: 1, fontSize: 14, height: 28 }}></td>
                        <td style={{ minWidth: 80, lineHeight: 1, fontSize: 14, height: 28 }}>Folder</td>
                        <td style={{ textAlign: 'left', minWidth: 120, lineHeight: 1, height: 28, paddingLeft: 12 }}>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: '#e0e7ff', borderRadius: 20, padding: '7px 22px', boxShadow: '0 2px 8px #c7d2fe', fontWeight: 700, fontSize: 15, color: '#2563eb', minWidth: 120 }}>
                            <span style={{ background: '#2563eb', color: '#fff', borderRadius: '50%', width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 16, boxShadow: '0 2px 8px #2563eb33', marginRight: 8 }}>
                              {(() => {
                                function collectIds(f) { return [f.id, ...(f.children || []).flatMap(collectIds)]; }
                                const allIds = collectIds(folder);
                                function countAutomations(node) {
                                  let count = allIds.includes(node.id) ? (node.automations?.length || 0) : 0;
                                  for (const child of (node.children || [])) {
                                    count += countAutomations(child);
                                  }
                                  return count;
                                }
                                return (automationTree || []).reduce((acc, node) => acc + countAutomations(node), 0);
                              })()}
                            </span>
                            automatyzacji
                          </span>
                        </td>
                        <td style={{ textAlign: 'left', minWidth: 80, lineHeight: 1, height: 28, paddingRight: 12 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'flex-start' }}>
                            <button className="icon-button edit enhanced-ghost-btn" title="Edytuj folder" style={{ display: 'flex', alignItems: 'center', background: '#f3f6fd', border: 'none', borderRadius: 10, padding: 7, marginRight: 5, transition: 'background 0.18s, box-shadow 0.18s', boxShadow: '0 2px 8px #e0e7ff' }} onClick={() => handleEditFolder(folder)}>
                              <Edit size={15} />
                            </button>
                            <button className="icon-button delete enhanced-ghost-btn" title="Usuń folder" style={{ display: 'flex', alignItems: 'center', background: '#f3f6fd', border: 'none', borderRadius: 10, padding: 7, marginRight: 10, transition: 'background 0.18s, box-shadow 0.18s', boxShadow: '0 2px 8px #e0e7ff' }} onClick={() => setIsDeletingFolder(folder.id)}>
                              <Trash2 size={15} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    )),
                    ...automationsList.map(automation => (
                      <tr
                        key={automation.id}
                        className={`enhanced-table-row automation-row-pro${hoveredRowId === automation.id ? ' row-hover' : ''}`}
                        style={{ background: '#fff', transition: 'background 0.18s', height: 20 }}
                        draggable
                        onMouseEnter={() => setHoveredRowId(automation.id)}
                        onMouseLeave={() => setHoveredRowId(null)}
                        onDragStart={() => setDraggedAutomationId(automation.id)}
                        onDragEnd={() => setDraggedAutomationId(null)}
                      >
                        <td style={{ fontWeight: 700, padding: '0 0 0 18px', minWidth: 140, lineHeight: 1, fontSize: 15, height: 28, verticalAlign: 'middle', cursor: 'pointer', color: '#2563eb' }}
                        >
                          <span style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
                            <FileSpreadsheet size={20} color="#1976d2" style={{ flexShrink: 0, marginRight: 8, filter: 'drop-shadow(0 2px 6px #c7d2fe)'}} />
                            <a style={{ color: '#18181b' }}>{automation.title}</a>
                          </span>
                        </td>
                        <td style={{ minWidth: 90, lineHeight: 1, fontSize: 14, height: 28 }}
                          title={automation.createdAt ? formatDateTime(automation.createdAt).full : ''}
                        >
                          {automation.createdAt ? (
                            <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                              <span style={{ color: '#666', fontWeight: 400, fontSize: 15 }}>{formatDateTime(automation.createdAt).label}</span>
                              <span style={{ color: '#888', marginLeft: 8, display: 'inline-flex', alignItems: 'center', fontWeight: 400, fontSize: 15 }}>
                                <Clock size={14} style={{ marginRight: 3, color: '#2563eb', verticalAlign: 'middle' }} />
                                {formatDateTime(automation.createdAt).time}
                              </span>
                            </span>
                          ) : '—'}
                        </td>
                        <td style={{ minWidth: 80, lineHeight: 1, fontSize: 14, height: 28 }}>{automation.automationTemplateDocument?.title || automation.automationTemplateDocument?.fileName || '—'}</td>
                        <td style={{ minWidth: 80, lineHeight: 1, fontSize: 14, height: 28 }}>{getAutomationLabel(automation.type)}</td>
                        <td style={{ textAlign: 'left', minWidth: 120, lineHeight: 1, height: 28, paddingLeft: 12 }}>
                          <div className="action-buttons" style={{ display: 'flex', gap: 10, justifyContent: 'flex-start', alignItems: 'center', marginRight: 0 }}>
                            <Button className="ghost-template-btn enhanced-chip-btn" style={{padding: '6px 18px', fontSize: 14, height: 32, borderRadius: 18, background: '#f3f6fd', color: '#2563eb', fontWeight: 700, boxShadow: '0 2px 8px #e0e7ff', border: 'none', display: 'flex', alignItems: 'center', gap: 8, transition: 'background 0.18s, box-shadow 0.18s'}} onClick={() => handleUseManually(automation)}>
                              <FormIcon style={{ width: 16, height: 16, marginRight: 0 }} /> Formularz
                            </Button>
                            <Button className="ghost-template-btn enhanced-chip-btn" style={{padding: '6px 18px', fontSize: 14, height: 32, borderRadius: 18, background: '#f3f6fd', color: '#22c55e', fontWeight: 700, boxShadow: '0 2px 8px #e0e7ff', border: 'none', display: 'flex', alignItems: 'center', gap: 8, transition: 'background 0.18s, box-shadow 0.18s'}} onClick={() => { setSelectedExcelAutomation(automation); setIsExcelModalOpen(true); }}>
                              <ExcelIcon style={{ width: 16, height: 16, marginRight: 0 }} /> Excel
                            </Button>
                          </div>
                        </td>
                        <td style={{ textAlign: 'left', minWidth: 80, lineHeight: 1, height: 28, paddingRight: 12 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'flex-start' }}>
                              <button className="icon-button edit enhanced-ghost-btn" title="Edytuj" style={{ display: 'flex', alignItems: 'center', background: '#f3f6fd', border: 'none', borderRadius: 10, padding: 7, marginRight: 5, transition: 'background 0.18s, box-shadow 0.18s', boxShadow: '0 2px 8px #e0e7ff' }} onClick={() => {
                              setIsEditMode(true);
                              setEditingAutomation(automation);
                              setAutomationTitle(automation.title);
                              setSelectedDocumentId(automation.id);
                              setIsAddAutomationOpen(true);
                            }}>
                              <Edit size={15} />
                            </button>
                            <button className="icon-button delete enhanced-ghost-btn" title="Usuń automatyzację" style={{ display: 'flex', alignItems: 'center', background: '#f3f6fd', border: 'none', borderRadius: 10, padding: 7, marginRight: 10, transition: 'background 0.18s, box-shadow 0.18s', boxShadow: '0 2px 8px #e0e7ff' }} onClick={() => handleDeleteAutomation(automation.id)}>
                              <Trash2 size={15} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ];
                })()
              )}
            </tbody>
          </table>
          {loading && (
            <div style={{
              position: 'absolute',
              top: 0, left: 0, right: 0, bottom: 0,
              background: 'rgba(255,255,255,0.6)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10
            }}>
              <Loader />
            </div>
          )}
        </div>

        {/* 🔥 Modal dla ręcznego użycia */}
        {isVariableModalOpen && selectedAutomation && selectedAutomation.id && tenantId && (
          <VariableModal
            isOpen={isVariableModalOpen}
            onClose={() => { setIsVariableModalOpen(false); setSelectedAutomation(null); }}
            variables={selectedAutomation.autofillVariables || []}
            documentId={selectedAutomation.id}
            tenantId={tenantId}
            onSuccess={(message, type) => setToast({ message, type })}
            folderId={selectedAutomation.folderId || null}
            selectedAutomation={selectedAutomation}
          />
        )}

        {isAgendaOpen && (
          <div className="modal-overlay" onClick={() => setIsAgendaOpen(false)}>
            <div className="modal-content upload-modal" style={{maxWidth: 480, minWidth: 0, padding: '40px 32px 32px 32px'}} onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h2 style={{fontSize: '1.35rem', fontWeight: 700, margin: 0}}>Jak przygotować szablon?</h2>
                <button className="close-button" style={{marginLeft: 12}} onClick={() => setIsAgendaOpen(false)}>
                  <X size={20} />
                </button>
              </div>
              <div style={{margin: '0 0 24px 0', color: '#1a1a1a', fontSize: 17, fontWeight: 500, textAlign: 'left', width: '100%', lineHeight: 1.6, letterSpacing: '-0.2px'}}>
                <ul style={{paddingLeft: 22, margin: 0}}>
                  <li>Generowanie dokumentów:
                    <ul style={{paddingLeft: 22, marginTop: 8, marginBottom: 8}}>
                      <li>Formularz - generuje pojedynczy dokument na podstawie wprowadzonych danych</li>
                      <li>Excel - generuje wiele dokumentów na podstawie danych z pliku Excel:
                        <ul style={{paddingLeft: 22, marginTop: 8}}>
                          <li>Pobierz szablon Excel</li>
                          <li>Wypełnij dane w szablonie</li>
                          <li>Wgraj wypełniony plik Excel</li>
                        </ul>
                      </li>
                    </ul>
                  </li>
                  <li>Lokalizacja wygenerowanych plików:
                    <ul style={{paddingLeft: 22, marginTop: 8}}>
                      <li>Możesz wybrać, czy dokumenty mają być generowane w folderze automatyzacji</li>
                      <li>Jeśli zaznaczysz tę opcję, dokumenty zostaną automatycznie umieszczone w odpowiednim folderze</li>
                    </ul>
                  </li>
                </ul>
              </div>
              <div className="modal-footer" style={{gap: 16, marginTop: 0, justifyContent: 'flex-end'}}>
                <Button className="upload-button" style={{padding: '10px 24px', fontSize: 16}} onClick={() => setIsAgendaOpen(false)}>Zamknij</Button>
              </div>
            </div>
          </div>
        )}

        {/* Modal dodawania automatyzacji */}
        {isAddAutomationOpen && (
          <div className="modal-overlay" onClick={() => {
            setIsAddAutomationOpen(false);
            setIsEditMode(false);
            setEditingAutomation(null);
            setAutomationTitle("");
            setSelectedDocumentId("");
            setSearchDoc("");
            setIsDocDropdownOpen(false);
          }}>
            <div className="modal-content upload-modal" style={{
              minWidth: 800,
              maxWidth: 1000,
              minHeight: 250,
              maxHeight: '90vh',
              padding: '40px 32px 48px 32px',
              borderRadius: 20
            }} onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h2 style={{ fontSize: '1.25rem', fontWeight: 700, marginLeft: 20 }}>{isEditMode ? 'Edytuj automatyzację' : 'Dodaj automatyzację'}</h2>
                <button className="close-button" style={{ marginLeft: 12 }} onClick={() => {
                  setIsAddAutomationOpen(false);
                  setIsEditMode(false);
                  setEditingAutomation(null);
                  setAutomationTitle("");
                  setSelectedDocumentId("");
                  setSearchDoc("");
                  setIsDocDropdownOpen(false);
                }}>
                  <X size={20} />
                </button>
              </div>
              <div style={{margin: '18px 0 18px 0', display: 'flex', flexDirection: 'column', alignItems: 'stretch', width: '100%'}}>
                <label className="modal-label">Nazwa automatyzacji</label>
                <input type="text" className="modal-input" value={automationTitle} onChange={e => setAutomationTitle(e.target.value)} placeholder="np. Automatyzacja faktury" style={{
                  marginBottom: 16,
                  marginTop: 5,
                  width: '100%',
                  boxSizing: 'border-box'
                }}/>
                <label className="modal-label">Wybierz dostępny szablon</label>
                <div style={{position: 'relative', width: '100%'}} ref={docInputRef}>
                  <input
                    type="text"
                    className="modal-input"
                    placeholder={
                      isEditMode && editingAutomation && (editingAutomation.automationTemplateDocument?.title || editingAutomation.automationTemplateDocument?.fileName)
                        ? (editingAutomation.automationTemplateDocument?.title || editingAutomation.automationTemplateDocument?.fileName)
                        : "Szukaj szablonu..."
                    }
                    value={
                      isEditMode && editingAutomation && !selectedDocumentId && !searchDoc
                        ? (editingAutomation.automationTemplateDocument?.title || editingAutomation.automationTemplateDocument?.fileName || "")
                        : (selectedDocumentId
                            ? (freeTemplates.find(t => t.id === selectedDocumentId)?.fileName || searchDoc)
                            : searchDoc)
                    }
                    onChange={e => {
                      setSearchDoc(e.target.value);
                      setSelectedDocumentId("");
                      setIsDocDropdownOpen(true);
                    }}
                    onFocus={() => setIsDocDropdownOpen(true)}
                    style={{
                      marginBottom: 8,
                      marginTop: 5,
                      cursor: 'pointer',
                      width: '100%',
                      boxSizing: 'border-box'
                    }}
                    readOnly={false}
                    disabled={false}
                  />
                  {/* Dropdown z podpowiedziami */}
                  <div
                    style={{
                      position: 'absolute',
                      left: 0,
                      right: 0,
                      top: 38,
                      zIndex: 1000,
                      background: '#fff',
                      border: '1px solid #e5e7eb',
                      borderRadius: 8,
                      boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
                      maxHeight: 115,
                      overflowY: 'auto',
                      display: isDocDropdownOpen ? 'block' : 'none',
                    }}
                  >
                    {isLoadingTemplates ? (
                      <div style={{padding: 12, color: '#888'}}>Ładowanie...</div>
                    ) : freeTemplates.length === 0 ? (
                      <div style={{padding: 12, color: '#888'}}>Brak wolnych szablonów</div>
                    ) : (
                      freeTemplates.map(t => (
                        <div
                          key={t.id}
                          style={{
                            padding: '10px 16px',
                            cursor: 'pointer',
                            background: selectedDocumentId === t.id ? '#e0e7ff' : 'transparent',
                            fontWeight: selectedDocumentId === t.id ? 600 : 400,
                          }}
                          onClick={() => {
                            setSelectedDocumentId(t.id);
                            setSearchDoc(t.fileName);
                            setIsDocDropdownOpen(false);
                          }}
                        >
                          {t.fileName}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
              <div className="modal-footer" style={{gap: 16, marginTop: 22, justifyContent: 'flex-end'}}>
                <Button className="outline-button" style={{padding: '10px 24px', fontSize: 16}} onClick={() => {
                  setIsAddAutomationOpen(false);
                  setIsEditMode(false);
                  setEditingAutomation(null);
                  setAutomationTitle("");
                  setSelectedDocumentId("");
                  setSearchDoc("");
                  setIsDocDropdownOpen(false);
                }}>Anuluj</Button>
                <Button className="upload-button" style={{padding: '10px 24px', fontSize: 16}} disabled={!automationTitle || !selectedDocumentId || isSubmitting} onClick={async () => {
                  setIsSubmitting(true);
                  try {
                    if (!tenantId) return;
                    if (isEditMode && editingAutomation) {
                      await updateAutomation(tenantId, editingAutomation.id, automationTitle, selectedDocumentId, currentFolderId || undefined);
                      setToast({ message: "Automatyzacja zaktualizowana!", type: "success" });
                    } else {
                      await addAutomation(tenantId, selectedDocumentId, automationTitle, currentFolderId || undefined);
                      setToast({ message: "Automatyzacja została dodana!", type: "success" });
                    }
                    setIsAddAutomationOpen(false);
                    setIsEditMode(false);
                    setEditingAutomation(null);
                    setAutomationTitle("");
                    setSelectedDocumentId("");
                    setSearchDoc("");
                    setIsDocDropdownOpen(false);
                    await loadAutomationTree();
                  } catch (e) {
                    setToast({ message: isEditMode ? "Błąd edycji automatyzacji." : "Błąd dodawania automatyzacji.", type: "error" });
                  } finally {
                    setIsSubmitting(false);
                  }
                }}>{isEditMode ? 'Zapisz zmiany' : 'Dodaj automatyzację'}</Button>
              </div>
            </div>
          </div>
        )}

        {isExcelModalOpen && (
          <div className="modal-overlay" onClick={() => { setIsExcelModalOpen(false); setSelectedExcelAutomation(null); }}>
            <div className="modal-content upload-modal" style={{ minWidth: 450, maxWidth: 620, minHeight: 260, maxHeight: '85vh', padding: '32px 24px 24px 24px', borderRadius: 18, boxShadow: '0 8px 32px rgba(37,99,235,0.13)', display: 'flex', flexDirection: 'column', alignItems: 'center' }} onClick={e => e.stopPropagation()}>
              <div className="modal-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18, width: '100%' }}>
                <h2 style={{ fontSize: '1.18rem', fontWeight: 700, margin: 0, color: '#222' }}>Wczytaj plik Excel</h2>
                <button className="close-button" style={{ marginLeft: 12 }} onClick={() => { setIsExcelModalOpen(false); setSelectedExcelAutomation(null); }}>
                  <X size={20} />
                </button>
              </div>
              <div style={{ fontSize: 15, color: '#444', marginBottom: 18, lineHeight: 1.6, background: '#f6f8fa', borderRadius: 8, padding: '12px 14px', fontWeight: 500, width: '100%' }}>
                <div style={{ marginBottom: 4 }}>
                  <b>1.</b> Pobierz przykładowy szablon Excel i uzupełnij swoje dane według wzoru.
                </div>
                <div><b>2.</b> Wgraj gotowy plik, aby wygenerować dokumenty dla tej automatyzacji.</div>
              </div>
              <Button className="ghost-template-btn" style={{ fontSize: 15, padding: '8px 18px', display: 'flex', alignItems: 'center', gap: 8, marginBottom: 18 }} onClick={() => handleDownloadExcelTemplate(selectedExcelAutomation?.id)} disabled={!selectedExcelAutomation?.id}>
                <svg width="18" height="18" fill="none" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                Pobierz szablon Excel
              </Button>
              <div style={{ fontSize: 15, color: '#444', marginBottom: 18, lineHeight: 1.6, background: '#f6f8fa', borderRadius: 8, padding: '12px 14px', fontWeight: 500, width: '100%' }}>
                <div><b>3.</b> Wgraj gotowy plik, aby wygenerować dokumenty.</div>
              </div>
              <div {...getExcelRootProps()} style={{
                border: '2px dashed #e5e7eb',
                borderRadius: 12,
                padding: '38px 18px',
                textAlign: 'center',
                background: isExcelDragActive ? '#eff6ff' : '#f9fafb',
                marginBottom: 18,
                width: '100%',
                maxWidth: 340,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                cursor: 'pointer',
                transition: 'all 0.2s',
                boxShadow: isExcelDragActive ? '0 0 0 2px #2563eb33' : 'none',
              }}>
                <input {...getExcelInputProps()} />
                <ExcelIcon style={{ width: 48, height: 48, marginRight: 0 }} />
                <div style={{ fontSize: 18, color: '#4b5563', fontWeight: 500, marginBottom: 4 }}>
                  Przeciągnij i upuść plik Excel tutaj<br />lub kliknij, aby wybrać
                </div>
                <div style={{ fontSize: 15, color: '#6b7280', marginTop: 6 }}>
                  Obsługiwane formaty: <b>.xlsx, .xls</b> (max 10MB)
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 18, width: '100%' }}>
                <input
                  type="checkbox"
                  id="excelCustomLocationCheckbox"
                  checked={customLocation}
                  onChange={e => setCustomLocation(e.target.checked)}
                  className="custom-checkbox"
                  style={{ marginRight: 10 }}
                />
                <label htmlFor="excelCustomLocationCheckbox" className="custom-checkbox-label">
                  <span style={{ fontWeight: 600, color: '#222', fontSize: 15, letterSpacing: 0.1, marginLeft: 2 }}>
                    Generuj dokumenty w tym folderze co automatyzacja
                  </span>
                </label>
              </div>
              {excelFileName && (
                <div style={{ marginTop: 8, fontSize: 14, color: '#2563eb', fontWeight: 600 }}>
                  Wybrany plik: {excelFileName}
                </div>
              )}
              <div className="modal-footer" style={{ gap: 16, marginTop: 'auto', justifyContent: 'flex-end', display: 'flex', width: '100%', paddingTop: 16 }}>
                <Button className="outline-button" style={{ padding: '10px 24px', fontSize: 16 }} onClick={() => setIsExcelModalOpen(false)}>Anuluj</Button>
                <Button className="upload-button" style={{ padding: '10px 24px', fontSize: 16 }} disabled={!excelFile || isExcelSubmitting} onClick={handleExcelSubmit}>
                  {isExcelSubmitting ? <Loader2 size={20} className="animate-spin" /> : "Generuj"}
                </Button>
              </div>
              {excelToast && (
                <Toast message={excelToast.message} type={excelToast.type} onClose={() => setExcelToast(null)} />
              )}
            </div>
          </div>
        )}

        {isAddFolderOpen && (
          <div className="modal-overlay" onClick={() => setIsAddFolderOpen(false)}>
            <div className="modal-content upload-modal" style={{ minWidth: 350, maxWidth: 400, padding: '32px 24px', borderRadius: 16 }} onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h2 style={{ fontSize: '1.18rem', fontWeight: 700, margin: 0 }}>Dodaj folder</h2>
                <button className="close-button" onClick={() => setIsAddFolderOpen(false)}><X size={20} /></button>
              </div>
              <input type="text" className="modal-input" placeholder="Nazwa folderu" value={newFolderName} onChange={e => setNewFolderName(e.target.value)} style={{ margin: '18px 0' }} />
              <div className="modal-footer" style={{ gap: 16, justifyContent: 'flex-end' }}>
                <Button className="outline-button" onClick={() => setIsAddFolderOpen(false)}>Anuluj</Button>
                <Button className="upload-button" disabled={!newFolderName || isAddFolderLoading} onClick={async () => {
                  if (!tenantId || !newFolderName) return;
                  setIsAddFolderLoading(true);
                  try {
                    await handleAddFolder(newFolderName);
                    setNewFolderName("");
                    setIsAddFolderOpen(false);
                  } catch (e) {
                    // Do nothing here, toast is shown in handleAddFolder
                  } finally {
                    setIsAddFolderLoading(false);
                  }
                }}>{isAddFolderLoading ? 'Dodawanie...' : 'Dodaj'}</Button>
              </div>
            </div>
          </div>
        )}

        {isDeletingFolder && (
          <div className="modal-overlay" onClick={() => setIsDeletingFolder(null)}>
            <div className="modal-content upload-modal" style={{ minWidth: 350, maxWidth: 400, padding: '32px 24px', borderRadius: 16 }} onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h2 style={{ fontSize: '1.18rem', fontWeight: 700, margin: 0 }}>Usuń folder</h2>
                <button className="close-button" onClick={() => setIsDeletingFolder(null)}><X size={20} /></button>
              </div>
              <div style={{ margin: '18px 0', fontSize: 16 }}>Czy na pewno chcesz usunąć ten folder? Wszystkie foldery i automatyzacje w folderze zostaną usunięte.</div>
              <div className="modal-footer" style={{ gap: 16, justifyContent: 'flex-end' }}>
                <Button className="outline-button" onClick={() => setIsDeletingFolder(null)}>Anuluj</Button>
                <Button className="upload-button" onClick={async () => {
                  if (!tenantId || !isDeletingFolder) return;
                  try {
                    await deleteAutomationFolder(tenantId, isDeletingFolder);
                    setIsDeletingFolder(null);
                    await loadAutomations(); // przeładuj widok folderu po usunięciu folderu
                    await loadAutomationTree(); // przeładuj drzewo
                  } catch {
                    setToast({ message: "Błąd usuwania folderu.", type: "error" });
                  }
                }}>Usuń</Button>
              </div>
            </div>
          </div>
        )}

        {isEditingFolder && (
          <div className="modal-overlay" onClick={() => setIsEditingFolder(null)}>
            <div className="modal-content upload-modal" style={{ minWidth: 350, maxWidth: 400, padding: '32px 24px', borderRadius: 16 }} onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h2 style={{ fontSize: '1.18rem', fontWeight: 700, margin: 0 }}>Zmień nazwę folderu</h2>
                <button className="close-button" onClick={() => setIsEditingFolder(null)}><X size={20} /></button>
              </div>
              <input type="text" className="modal-input" placeholder="Nazwa folderu" value={editFolderName} onChange={e => setEditFolderName(e.target.value)} style={{ margin: '18px 0' }} />
              {editFolderError && <div style={{ color: 'red', marginBottom: 8 }}>{editFolderError}</div>}
              <div className="modal-footer" style={{ gap: 16, justifyContent: 'flex-end' }}>
                <Button className="outline-button" onClick={() => setIsEditingFolder(null)}>Anuluj</Button>
                <Button className="upload-button" disabled={!editFolderName || isEditingFolderLoading} onClick={async () => {
                  if (!tenantId || !editFolderName) return;
                  setIsEditingFolderLoading(true);
                  setEditFolderError(null);
                  try {
                    await updateAutomationFolderName(tenantId, isEditingFolder.id, editFolderName);
                    setIsEditingFolder(null);
                    setEditFolderName("");
                    await loadAutomationTree();
                    setToast({ message: "Nazwa folderu została zmieniona!", type: "success" });
                  } catch {
                    setEditFolderError("Błąd zmiany nazwy folderu.");
                  } finally {
                    setIsEditingFolderLoading(false);
                  }
                }}>{isEditingFolderLoading ? 'Zapisywanie...' : 'Zapisz'}</Button>
              </div>
            </div>
          </div>
        )}

        {toast && (
          <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
        )}
        <style>{`
          .enhanced-header {
            margin-bottom: 5px !important;
          }
          .enhanced-primary-btn:hover, .enhanced-primary-btn:focus {
            background: linear-gradient(90deg, #1746a0 0%, #2563eb 100%) !important;
            box-shadow: 0 6px 24px #2563eb33 !important;
            outline: 2px solid #2563eb44;
          }
          .enhanced-secondary-btn:hover, .enhanced-secondary-btn:focus {
            background: #f3f6fd !important;
            box-shadow: 0 4px 16px #c7d2fe !important;
            outline: 2px solid #2563eb22;
          }
          .enhanced-table-container {
            margin-top: 0 !important;
            padding: 0 0 18px 0 !important;
            box-shadow: 0 4px 32px rgba(37,99,235,0.07) !important;
            border-radius: 18px !important;
            background: #fff !important;
          }
          .enhanced-table thead tr {
            background: #f3f6fd !important;
            border-bottom: 2.5px solid #e0e7ff !important;
            height: 56px !important;
          }
          .enhanced-table th {
            font-weight: 800 !important;
            font-size: 17px !important;
            padding: 16px 0 16px 18px !important;
            color: #222 !important;
            background: #f3f6fd !important;
            letter-spacing: 0.02em;
            text-transform: uppercase;
          }
          .enhanced-table td {
            font-size: 15px !important;
            font-weight: 600 !important;
            padding: 10px 0 10px 18px !important;
            background: #fff !important;
            border-bottom: 1.5px solid #f1f5f9 !important;
            transition: background 0.18s, box-shadow 0.18s;
          }
          .enhanced-table tr:hover {
            background: #f0f6ff !important;
            box-shadow: 0 2px 12px #c7d2fe44 !important;
          }
          .automation-badge-link:hover, .automation-badge-link:focus {
            background: #dbeafe !important;
            box-shadow: 0 4px 16px #2563eb33 !important;
            outline: 2px solid #2563eb44;
          }
          .automation-badge-link:active {
            background: #c7d2fe !important;
            box-shadow: 0 2px 8px #2563eb33 !important;
          }
          .automation-badge-bubble {
            background: #2563eb !important;
            color: #fff !important;
            border-radius: 50% !important;
            width: 28px !important;
            height: 28px !important;
            display: flex !important;
            alignItems: center !important;
            justifyContent: center !important;
            font-weight: 800 !important;
            font-size: 16px !important;
            box-shadow: 0 2px 8px #2563eb33 !important;
            margin-right: 8px !important;
          }
          .enhanced-chip-btn:hover, .enhanced-chip-btn:focus {
            background: #e0e7ff !important;
            box-shadow: 0 4px 16px #c7d2fe !important;
            outline: 2px solid #2563eb22;
          }
          .enhanced-ghost-btn:hover, .enhanced-ghost-btn:focus {
            background: #e0e7ff !important;
            box-shadow: 0 4px 16px #c7d2fe !important;
            outline: 2px solid #2563eb22;
          }
          .enhanced-table-container::-webkit-scrollbar {
            background: #e0e7ff;
            border-radius: 8px;
          }
          .enhanced-table-container::-webkit-scrollbar-thumb {
            background: #c7d2fe;
            border-radius: 8px;
          }
          @media (max-width: 900px) {
            .enhanced-header {
              flex-direction: column;
              align-items: flex-start;
              padding: 18px 10px 18px 10px !important;
              gap: 18px;
            }
            .enhanced-table th, .enhanced-table td {
              font-size: 14px !important;
              padding-left: 8px !important;
            }
          }
          @media (max-width: 600px) {
            .enhanced-header {
              flex-direction: column;
              align-items: flex-start;
              padding: 12px 4px 12px 4px !important;
              gap: 10px;
            }
            .enhanced-table th, .enhanced-table td {
              font-size: 13px !important;
              padding-left: 4px !important;
            }
            .enhanced-table-container {
              padding: 0 0 8px 0 !important;
            }
          }
          .icon-button[title] {
            position: relative;
          }
          .icon-button[title]:hover::after, .icon-button[title]:focus::after {
            content: attr(title);
            position: absolute;
            top: -32px;
            right: 0;
            background: #222;
            color: #fff;
            font-size: 13px;
            padding: 4px 10px;
            border-radius: 8px;
            white-space: nowrap;
            z-index: 100;
            opacity: 0.95;
            pointer-events: none;
          }
          .enhanced-table-row:hover {
            background: #f0f6ff !important;
            box-shadow: 0 2px 12px #c7d2fe44 !important;
          }
          .icon-button.enhanced-ghost-btn:hover, .icon-button.enhanced-ghost-btn:focus, .ghost-template-btn.enhanced-chip-btn:hover, .ghost-template-btn.enhanced-chip-btn:focus {
            background: #e0e7ff !important;
            box-shadow: 0 4px 16px #c7d2fe !important;
            outline: 2px solid #2563eb22;
            transition: background 0.18s, box-shadow 0.18s;
          }
        `}</style>
      </>
    );
  }
);

export default AutomationList;
